// Copyright (c) StrangeBee 2023

import React, { ReactElement, useMemo } from 'react';
import { Store } from 'antd/lib/form/interface';
import { useLocalMessage } from '@Helpers/';
import { FormInstance } from 'antd/lib/form';
import { BulkEditFormField } from '@Molecules/BulkEditFormField/BulkEditFormField';
import { THForm } from '@Molecules/THForm/THForm';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { THAutoComplete } from '@Molecules/Inputs/THAutoComplete/THAutoComplete';
import { TaskOutput, UserOutput } from 'thehive-sdk';
import { IconLabel } from '@Atoms/Info';
import UserAvatar from '@Atoms/UserAvatar/UserAvatar';

export interface TaskBulkEditValues extends Store {
    group?: string;
    assignee?: string;
}

interface TaskBulkEditFormProps {
    onFinish: (values: TaskBulkEditValues) => void;
    form: FormInstance;
    isValid: boolean;
    onChange: () => void;
    taskList?: TaskOutput[];
    assignableUsers?: UserOutput[];
    onCancel: () => void;
}

export const TaskBulkEditForm = ({
    onFinish,
    form,
    isValid,
    onChange,
    taskList,
    assignableUsers,
    onCancel,
}: TaskBulkEditFormProps): ReactElement => {
    const i = useLocalMessage();

    const groupList = useMemo(
        () =>
            taskList?.reduce(
                (acc, { group }) => (group && !acc.includes(group) ? [...acc, group] : acc),
                [] as string[],
            ),
        [taskList],
    );

    const onValuesChange = (changedValue: Store): void => {
        const valueName = Object.keys(changedValue)[0] || '';

        if (!valueName.includes('Checked')) {
            form.setFieldsValue({ [`${valueName}Checked`]: true });
        }
        onChange();
    };

    return (
        <THForm<TaskBulkEditValues>
            name="task-bulk-edit-form"
            onValuesChange={onValuesChange}
            form={form}
            onFinish={onFinish}
        >
            <THForm.Layout footer={<FormSubmitButton onCancel={onCancel} disabled={!isValid} />}>
                <BulkEditFormField
                    label={i('task.create.form.group.label')}
                    name="group"
                    input={<THAutoComplete options={groupList?.map((value) => ({ label: value, value }))} />}
                />

                <BulkEditFormField
                    label={i('task.create.form.assignee.label')}
                    name="assignee"
                    input={
                        <THSelect
                            allowClear
                            showSearch
                            borderless
                            placeholder={i('task.create.form.assignee.placeholder')}
                        >
                            {assignableUsers?.map(({ _id, name, avatar, login }) => (
                                <THSelect.Option key={_id} value={login}>
                                    <IconLabel
                                        key={_id}
                                        icon={<UserAvatar name={name} avatar={avatar} />}
                                        label={name}
                                    />
                                </THSelect.Option>
                            ))}
                        </THSelect>
                    }
                />
            </THForm.Layout>
        </THForm>
    );
};
